import { Dispatch, SetStateAction, useState } from 'react';
import slugify from 'slugify';
import { useRouter } from 'next/router';

import { generateDropdownOptionsRenderer, generateCheckboxOptionsRenderer } from './renderers';

import type {
    ActiveFilter,
    HandleFilter,
    FilterResults,
    GenerateDropdownOptions,
    GenerateCheckboxOptions
} from '.';

export interface UseFilters {
    state: {
        activeFilters: ActiveFilter[];
        searchTerm: string | undefined;
    };
    actions: {
        removeFilter: (id: string) => void;
        filterResults: FilterResults;
        setSearchTerm: Dispatch<SetStateAction<string | undefined>>;
        resetActiveFilters: () => void;
    };
    ui: {
        generateDropdownOptions: GenerateDropdownOptions;
        generateCheckboxOptions: GenerateCheckboxOptions;
    };
}

const useFilters = (
    initialActiveFilters: ActiveFilter[],
    initialSearchTerm?: string
): UseFilters => {
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialActiveFilters);
    const [searchTerm, setSearchTerm] = useState<string | undefined>(initialSearchTerm);

    const router = useRouter();
    const pathname = router.pathname;

    const addFilter = (id: string, type: string, label: string) => {
        setActiveFilters(prev => [...prev, { id, type, label }]);
    };

    const removeFilter = (id: string) => {
        setActiveFilters(prev => prev.filter(filter => filter.id !== id));
    };

    const handleFilter: HandleFilter = (activeFilter, id, type, label) => {
        if (activeFilter) {
            removeFilter(id);
        } else {
            addFilter(id, type, label);
        }
    };

    const filterResults: FilterResults = extras => {
        const query = new URLSearchParams();

        activeFilters.forEach(filter => {
            const key = slugify(filter.type);
            if (query.has(key)) {
                query.set(key, `${query.get(key)},${filter.id}`);
            } else {
                query.append(key, filter.id);
            }
        });

        if (searchTerm) {
            query.set('search', searchTerm);
        }

        if (extras) {
            extras.map(extra => query.set(extra.parameter, extra.value));
        }

        query.set('page', '1');

        const qs = query.toString();
        const newUrl = `${pathname}?${qs}`;

        router.push(newUrl, undefined, { scroll: false });
    };

    const generateDropdownOptions: GenerateDropdownOptions = filter =>
        generateDropdownOptionsRenderer(filter, activeFilters, handleFilter);

    const generateCheckboxOptions: GenerateCheckboxOptions = (filter, type) =>
        generateCheckboxOptionsRenderer(filter, type, activeFilters, handleFilter);

    const resetActiveFilters = () => {
        setActiveFilters([]);
    };

    return {
        state: {
            activeFilters,
            searchTerm
        },
        actions: {
            removeFilter,
            filterResults,
            setSearchTerm,
            resetActiveFilters
        },
        ui: {
            generateDropdownOptions,
            generateCheckboxOptions
        }
    };
};

export default useFilters;
